import { useEffect, useRef, useState } from 'react';
import { AlertTriangle, BookOpen, Check, ChevronDown, History, MessageSquare, Minus, Thermometer, User, X } from 'lucide-react';
import {
  describeRange, evaluateTemperature, FIX_BY_LABELS, FIX_BY_WINDOWS, recurrenceTier, type FixByWindow, type Result, type TemplateItem,
} from '@bp/shared';
import type { PreviousItem, ResponseRow } from '@/lib/types';
import { cn, fmtDate, fmtDateTime } from '@/lib/utils';
import { ActionStatusBadge } from '../badges';
import { Badge, Textarea } from '../ui';
import { CaptureButtons, MediaThumbs, type Thumb } from './Media';

export type ItemPatch = { result?: Result; temperature?: number | null; comment?: string | null; fix_by_window?: FixByWindow | null };

const resultBtn: Record<'pass' | 'fail' | 'na', { on: string; label: string; icon: typeof Check }> = {
  pass: { on: 'bg-pass text-white border-pass', label: 'Pass', icon: Check },
  fail: { on: 'bg-fail text-white border-fail', label: 'Fail', icon: X },
  na: { on: 'bg-slate-500 text-white border-slate-500', label: 'N/A', icon: Minus },
};

const tierText = { low: 'Failed before', medium: 'Repeat failure', high: 'Chronic failure' } as const;

export function ItemCard({ item, response, previous, media, index, onChange, onFiles, onDeleteMedia }: {
  item: TemplateItem;
  response: ResponseRow;
  previous?: PreviousItem;
  media: Thumb[];
  index: number;
  onChange: (patch: ItemPatch) => void;
  onFiles: (files: File[]) => void;
  onDeleteMedia: (id: string) => void;
}) {
  const r = response.result;
  const tier = recurrenceTier(response.prior_failure_count);
  const [showComment, setShowComment] = useState(!!response.comment);
  const [showPrev, setShowPrev] = useState(false);
  const [comment, setComment] = useState(response.comment ?? '');
  const [temp, setTemp] = useState(response.temperature?.toString() ?? '');
  const commentTimer = useRef<number | undefined>(undefined);
  const tempTimer = useRef<number | undefined>(undefined);

  // Keep local text in sync if the server copy changes (e.g. another device).
  useEffect(() => setComment((c) => (document.activeElement?.id === `c-${item.key}` ? c : response.comment ?? '')), [response.comment, item.key]);
  useEffect(() => setTemp((t) => (document.activeElement?.id === `t-${item.key}` ? t : response.temperature?.toString() ?? '')), [response.temperature, item.key]);

  const saveComment = (v: string) => {
    window.clearTimeout(commentTimer.current);
    commentTimer.current = window.setTimeout(() => onChange({ comment: v.trim() ? v : null }), 700);
  };
  const saveTemp = (v: string) => {
    window.clearTimeout(tempTimer.current);
    const n = v.trim() === '' ? null : Number(v);
    if (n !== null && !Number.isFinite(n)) return;
    tempTimer.current = window.setTimeout(() => onChange({ temperature: n }), 600);
  };
  const tempPreview = temp.trim() !== '' && Number.isFinite(Number(temp)) ? evaluateTemperature(Number(temp), item) : null;

  return (
    <div
      id={`item-${item.key}`}
      className={cn('rounded-xl border-2 bg-card p-4 transition-colors print-break',
        r === 'pass' && 'border-pass/40', r === 'fail' && 'border-fail/50 bg-fail-bg/30', r === 'na' && 'border-slate-300', r === 'pending' && 'border-border')}
    >
      <div className="flex items-start gap-3">
        <span className="tabular mt-0.5 w-6 flex-none text-sm text-muted-fg">{index}.</span>
        <div className="min-w-0 flex-1">
          <p className="text-[15px] leading-snug font-medium">{item.text}</p>
          <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
            {item.is_critical && <Badge tone="fail">Critical</Badge>}
            {tier !== 'none' && <Badge tone={tier === 'low' ? 'warn' : 'fail'}><AlertTriangle className="size-3" />{tierText[tier]} · {response.prior_failure_count}×</Badge>}
            {response.accountable_worker_name && <Badge><User className="size-3" />{response.accountable_worker_name}</Badge>}
            {item.input_type === 'temperature' && <Badge tone="info"><Thermometer className="size-3" />{describeRange(item)}</Badge>}
            {item.sop_url && (
              <a href={item.sop_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-info-bg px-2 py-0.5 text-xs font-medium text-info hover:underline">
                <BookOpen className="size-3" />SOP
              </a>
            )}
          </div>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap items-center gap-2 pl-9">
        {item.input_type === 'temperature' ? (
          <div className="flex items-center gap-2">
            <div className="relative">
              <input
                id={`t-${item.key}`}
                inputMode="decimal"
                type="number"
                step="0.1"
                placeholder="Reading"
                value={temp}
                disabled={r === 'na'}
                onChange={(e) => { setTemp(e.target.value); saveTemp(e.target.value); }}
                onBlur={(e) => { window.clearTimeout(tempTimer.current); const n = e.target.value.trim() === '' ? null : Number(e.target.value); if (n !== response.temperature) onChange({ temperature: n }); }}
                className={cn('h-12 w-36 rounded-lg border-2 bg-card pr-10 pl-3 text-lg font-semibold tabular',
                  tempPreview === 'pass' && 'border-pass text-pass', tempPreview === 'fail' && 'border-fail text-fail')}
              />
              <span className="absolute top-1/2 right-3 -translate-y-1/2 text-sm text-muted-fg">°{item.temp_unit}</span>
            </div>
            {tempPreview && <Badge tone={tempPreview === 'pass' ? 'pass' : 'fail'} className="text-sm">{tempPreview === 'pass' ? 'In range' : 'Out of range'}</Badge>}
            {item.allow_na && (
              <ResultButton kind="na" active={r === 'na'} onClick={() => onChange({ result: r === 'na' ? 'pending' : 'na' })} />
            )}
          </div>
        ) : (
          <>
            <ResultButton kind="pass" active={r === 'pass'} onClick={() => onChange({ result: r === 'pass' ? 'pending' : 'pass' })} />
            <ResultButton kind="fail" active={r === 'fail'} onClick={() => onChange({ result: r === 'fail' ? 'pending' : 'fail' })} />
            {item.allow_na && <ResultButton kind="na" active={r === 'na'} onClick={() => onChange({ result: r === 'na' ? 'pending' : 'na' })} />}
          </>
        )}
      </div>

      {r === 'fail' && (
        <div className="mt-3 pl-9">
          <p className="mb-1.5 text-xs font-semibold tracking-wide text-muted-fg uppercase">Fix by</p>
          <div className="flex flex-wrap gap-2">
            {FIX_BY_WINDOWS.map((w) => (
              <button
                key={w}
                type="button"
                onClick={() => onChange({ fix_by_window: response.fix_by_window === w ? null : w })}
                className={cn('h-9 rounded-full border px-3 text-sm font-medium', response.fix_by_window === w ? 'border-fail bg-fail text-white' : 'bg-card hover:bg-muted')}
              >
                {FIX_BY_LABELS[w]}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap items-center gap-2 pl-9">
        {item.allow_comment && (
          <button type="button" onClick={() => setShowComment((s) => !s)} className={cn('inline-flex h-10 items-center gap-2 rounded-lg border px-3 text-sm font-medium hover:bg-muted', comment && 'border-info text-info')}>
            <MessageSquare className="size-4" />{comment ? 'Note' : 'Add note'}
          </button>
        )}
        {(item.allow_photo || item.allow_video) && <CaptureButtons photo={item.allow_photo} video={item.allow_video} onFiles={onFiles} />}
        {previous && (
          <button type="button" onClick={() => setShowPrev((s) => !s)} className="inline-flex h-10 items-center gap-1.5 rounded-lg px-2 text-sm text-muted-fg hover:bg-muted">
            <History className="size-4" />Last time: <span className={cn('font-medium', previous.result === 'fail' ? 'text-fail' : previous.result === 'pass' ? 'text-pass' : '')}>{previous.result === 'na' ? 'N/A' : previous.result}</span>
            <ChevronDown className={cn('size-4 transition-transform', showPrev && 'rotate-180')} />
          </button>
        )}
      </div>

      {showComment && item.allow_comment && (
        <div className="mt-3 pl-9">
          <Textarea id={`c-${item.key}`} value={comment} placeholder="What did you see?" onChange={(e) => { setComment(e.target.value); saveComment(e.target.value); }} />
        </div>
      )}

      {media.length > 0 && <div className="mt-3 pl-9"><MediaThumbs items={media} onDelete={onDeleteMedia} /></div>}

      {showPrev && previous && (
        <div className="mt-3 ml-9 rounded-lg bg-muted px-3 py-2 text-sm">
          <p><span className="text-muted-fg">Previous official result:</span> <strong className="capitalize">{previous.result === 'na' ? 'N/A' : previous.result}</strong>{previous.temperature !== null && ` (${previous.temperature}°)`}</p>
          {previous.comment && <p className="mt-1">“{previous.comment}”</p>}
          {previous.fix_by_window && <p className="mt-1 text-muted-fg">Promised fix: {FIX_BY_LABELS[previous.fix_by_window]}</p>}
          {previous.action_item && (
            <p className="mt-1 flex flex-wrap items-center gap-2"><span className="text-muted-fg">Task:</span><ActionStatusBadge status={previous.action_item.status} overdueDays={previous.action_item.overdue ? 1 : 0} />{previous.action_item.due_date && <span className="text-muted-fg">due {fmtDate(previous.action_item.due_date, true)}</span>}</p>
          )}
        </div>
      )}

      {response.answered_at && <p className="mt-2 pl-9 text-xs text-muted-fg">Saved {fmtDateTime(response.answered_at)}</p>}
    </div>
  );
}

function ResultButton({ kind, active, onClick }: { kind: 'pass' | 'fail' | 'na'; active: boolean; onClick: () => void }) {
  const b = resultBtn[kind];
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn('inline-flex h-12 min-w-24 items-center justify-center gap-2 rounded-lg border-2 px-4 text-base font-semibold transition-colors', active ? b.on : 'border-border bg-card hover:bg-muted')}
    >
      <b.icon className="size-5" />{b.label}
    </button>
  );
}
